import { type AnimationClip, type Object3D, type SkinnedMesh } from 'three'
import { BoneNamingStructure } from './DownloadSettings.ts'
import { MixamoMapper } from '../../../retarget/bone-automap/MixamoMapper.ts'
import { UnityHumanoidMapper } from '../../../retarget/bone-automap/UnityHumanoidMapper.ts'

/**
 * Renames bones + animation tracks to a target game-engine naming convention
 * for export. Returns a `restore()` function the caller invokes after the
 * GLTFExporter has serialized — leaving the in-memory scene's bone names
 * untouched for continued editing.
 *
 * Each convention is registered as a `bone_lookup` function below; adding a
 * new one is a single-line dispatch entry.
 */
export class ExportBoneNamingService {
  public static apply_download_settings (
    skinned_meshes: SkinnedMesh[],
    animation_clips: AnimationClip[],
    bone_naming_structure: BoneNamingStructure
  ): () => void {
    const lookup = ExportBoneNamingService.lookup_for(bone_naming_structure)
    if (lookup === null) {
      return () => {}
    }

    const renamed_bones = new Map<Object3D, string>()
    ExportBoneNamingService.rename_bones(skinned_meshes, lookup, renamed_bones)
    ExportBoneNamingService.rename_animation_tracks(animation_clips, lookup)

    return () => {
      renamed_bones.forEach((original_name, bone_object) => {
        bone_object.name = original_name
      })
    }
  }

  /**
   * Returns the `(internal_name) → target_name | undefined` lookup function
   * for the given naming convention, or null when no renaming is needed
   * (Default just keeps the in-app names).
   */
  private static lookup_for (structure: BoneNamingStructure): ((source: string) => string | undefined) | null {
    switch (structure) {
      case BoneNamingStructure.Mixamo:
        return (source: string) => MixamoMapper.map_source_bone_name_to_mixamo(source)
      case BoneNamingStructure.UnityHumanoid:
        return (source: string) => UnityHumanoidMapper.map_source_bone_name(source)
      case BoneNamingStructure.Default:
      default:
        return null
    }
  }

  private static rename_bones (
    skinned_meshes: SkinnedMesh[],
    lookup: (source: string) => string | undefined,
    renamed_bones: Map<Object3D, string>
  ): void {
    skinned_meshes.forEach((skinned_mesh) => {
      skinned_mesh.traverse((scene_object) => {
        if (scene_object.type !== 'Bone') return
        const target_name = lookup(scene_object.name)
        if (target_name === undefined) return
        renamed_bones.set(scene_object, scene_object.name)
        scene_object.name = target_name
      })
    })
  }

  private static rename_animation_tracks (
    animation_clips: AnimationClip[],
    lookup: (source: string) => string | undefined
  ): void {
    animation_clips.forEach((clip) => {
      clip.tracks.forEach((track) => {
        track.name = ExportBoneNamingService.rename_track_name(track.name, lookup)
      })
    })
  }

  private static rename_track_name (
    track_name: string,
    lookup: (source: string) => string | undefined
  ): string {
    const first_dot = track_name.indexOf('.')
    if (first_dot > 0) {
      const node_path = track_name.slice(0, first_dot)
      const mapped_node_path = ExportBoneNamingService.rename_node_path(node_path, lookup)
      if (mapped_node_path !== node_path) {
        return `${mapped_node_path}${track_name.slice(first_dot)}`
      }
    }

    // Handle PropertyBinding names like .bones[bone_name].quaternion — used
    // when a SkinnedMesh's bones are accessed via the skeleton index syntax.
    return track_name.replace(/(\.bones\["?)([^\]"]+)("?\])/g, (match: string, prefix: string, bone_name: string, suffix: string) => {
      const target_name = lookup(bone_name)
      if (target_name === undefined) return match
      return `${prefix}${target_name}${suffix}`
    })
  }

  private static rename_node_path (
    node_path: string,
    lookup: (source: string) => string | undefined
  ): string {
    const direct = lookup(node_path)
    if (direct !== undefined) return direct
    const path_parts = node_path.split('/')
    const node_leaf_name = path_parts[path_parts.length - 1]
    const mapped_leaf_name = lookup(node_leaf_name)
    if (mapped_leaf_name === undefined) return node_path
    path_parts[path_parts.length - 1] = mapped_leaf_name
    return path_parts.join('/')
  }
}
